/**
 * Field width -> column span, on a 12-column section grid (TRUS-545 Phase 2).
 *
 * Shared by the two renderers that lay out a form's fields:
 *   - FormRenderer          — submitted/reviewed records, preview, public intake
 *   - FormInstanceEditor    — the DRAFT editor, which has its own field renderer
 *
 * Those two drifted once already: Phase 2 gave the grid to FormRenderer only, so
 * a form authored with half-width fields laid out correctly everywhere EXCEPT
 * the screen people actually write progress notes on. One definition, imported
 * twice, is what stops that happening again.
 */

/**
 * Every entry collapses to col-span-12 below `sm`: a two-column form on a phone
 * is a one-column form. Written out as whole literal class strings because
 * Tailwind's scanner reads source text — a computed `col-span-${n}` produces no
 * CSS at all and the field silently renders full width.
 */
export const WIDTH_SPAN: Record<string, string> = {
  full: 'col-span-12',
  two_thirds: 'col-span-12 sm:col-span-8',
  half: 'col-span-12 sm:col-span-6',
  third: 'col-span-12 sm:col-span-4',
  quarter: 'col-span-12 sm:col-span-3',
};

/**
 * Structural elements always take a whole row, whatever width was stored. A
 * half-width divider or section heading is never what the author meant, and a
 * field_group expands into N repeated instances so it needs the room.
 */
export const FULL_WIDTH_TYPES = new Set([
  'section_header',
  'divider',
  'spacer',
  'field_group',
  // TRUS-571 Layer 2 elements that head or span a block rather than sit in one.
  'panel_heading',
  'inline_alert',
  'progress_bar',
  'checklist_grid',
  'member_voice',
]);

/** Unknown widths fall back to full — see the `width` note in routers/forms.py. */
export function spanClassFor(field: {
  field_type?: string;
  width?: string;
  field_group_id?: number | null;
  validation?: Record<string, any> | null;
}): string {
  // field_group_id set without field_type === 'field_group' still means a
  // repeatable group: the draft editor checks both, so this must too.
  if (FULL_WIDTH_TYPES.has(field.field_type as string) || field.field_group_id) {
    return 'col-span-12';
  }
  // An AUTHORED width always wins — this is a default, not an override.
  if (field.width) {
    return WIDTH_SPAN[field.width] || 'col-span-12';
  }
  // A READ-ONLY field is a label and a value, not something to fill in, so it
  // does not need a full row. IFR's ECM Member Snapshot is 100 of them
  // (TRUS-571) and at one per row it is a wall of 100 lines before the first
  // question — the design lays them out as a grid. Two-up by default; still
  // one column below `sm`.
  if (field.validation?.read_only === true) {
    return WIDTH_SPAN.half;
  }
  return 'col-span-12';
}
